<script lang="ts">
  import { firmDetailStore, patchFirm } from "@entities/firm/store.svelte";
  import { sessionStore } from "@features/auth/session/model/store.svelte";
  import { timelineStore } from "@entities/timeline/store.svelte";
  import { contractListStore } from "@entities/contract/store.svelte";
  import {
    firmLawyersStore,
    setFirmRepresentatives,
  } from "@entities/lawyer/store.svelte";
  import { productOptionsStore } from "@entities/product/store.svelte";
  import { assignableAccountsStore } from "@entities/account/store.svelte";
  import FirmSummary from "@widgets/firm-summary/FirmSummary.svelte";
  import FirmLawyers from "@widgets/firm-lawyers/FirmLawyers.svelte";
  import Timeline from "@widgets/timeline/Timeline.svelte";
  import ContractTable from "@widgets/contract-table/ContractTable.svelte";
  import SalesActionPanel from "@widgets/sales-action-panel/SalesActionPanel.svelte";
  import CustomerMemoPanel from "@widgets/customer-memo-panel/CustomerMemoPanel.svelte";
  import ContractLineItemsEditor from "@widgets/contract-line-items-editor/ContractLineItemsEditor.svelte";
  import AccountSelect from "@shared/ui/AccountSelect.svelte";
  import TabCountBadge from "@shared/ui/TabCountBadge.svelte";
  import TabUpdateDot from "@shared/ui/TabUpdateDot.svelte";
  import { tabCountAriaLabel, tabStatusAriaLabel } from "@shared/ui/tab-count";
  import AddDialogueLogModal from "@features/dialogue-log-create/AddDialogueLogModal.svelte";
  import ContractCreateModal from "@features/contract-create/ContractCreateModal.svelte";
  import { contractLinkFlow } from "@features/contract-create/contract-link.svelte";
  import ContractTransitionModal from "@features/contract-transition/ContractTransitionModal.svelte";
  import ContractArchiveModal from "@features/contract-archive/ContractArchiveModal.svelte";
  import ContractRestoreModal from "@features/contract-restore/ContractRestoreModal.svelte";
  import { canEdit } from "@shared/types/session";
  import { listOpportunities } from "@shared/api/opportunities";
  import { listSalesActions } from "@shared/api/sales-actions";
  import { listContracts } from "@shared/api/contracts";
  import { listFirmMemos } from "@shared/api/customer-memos";
  import { listFormSubmissions } from "@shared/api/form-submissions";
  import { formCrmEnabled } from "@shared/config/features";
  import {
    getFirmGenericTabUpdates,
    markTabSeen,
  } from "@shared/api/customer-tab-last-seen";
  import { parsePaged } from "@shared/api/paging";
  import { salesActionListStore } from "@entities/sales-action/store.svelte";
  import { needsContractCreation } from "@features/sales-action-inline-edit/contract-followup";
  import { isCurrentAction } from "@features/sales-action-inline-edit/tab-filter";
  import type { Contract } from "@domain/contract/model";
  import { parseContract } from "@domain/contract/parseContract";
  import type { Opportunity } from "@domain/opportunity/model";
  import { parseOpportunity } from "@domain/opportunity/parseOpportunity";
  import type { TimelineQuery } from "@domain/timeline/model";
  import type { SalesAction } from "@shared/types/sales-action";
  import {
    assigneeDisplayName as resolveAssigneeName,
    formatHeaderDate,
    reasonOrUnset,
  } from "@domain/firm/detail-display";
  import { firmBulkSalesSummaryLabel } from "@domain/bulk-sales/labels";
  import type { DialogueLog } from "@shared/types/dialogue-log";
  import type { FormSubmission } from "@shared/types/form-submission";
  import type {
    FirmGenericTabUpdates,
    TabKey as CustomerTabKey,
  } from "@shared/types/customer-tab-last-seen";

  interface Props {
    id: string;
  }

  let { id }: Props = $props();

  type TabKey = "profile" | "sales" | "lawyers" | "deals" | "memos";
  type GenericCustomerTabKey = Exclude<CustomerTabKey, "sales_actions">;
  const COMPETITOR_USAGE_HASH = "#competitor-solution-usage";

  function hasCompetitorUsageHash(): boolean {
    return (
      typeof window !== "undefined" &&
      window.location.hash === COMPETITOR_USAGE_HASH
    );
  }

  function initialTab(): TabKey {
    return hasCompetitorUsageHash() ? "profile" : "sales";
  }

  function scrollToCompetitorUsage(): void {
    if (!hasCompetitorUsageHash() || typeof document === "undefined") return;
    window.setTimeout(() => {
      document
        .getElementById("competitor-solution-usage")
        ?.scrollIntoView({ block: "start" });
    }, 0);
  }

  // 営業担当が最初に営業アクションを確認できるよう、初期表示は sales タブ。
  let activeTab = $state<TabKey>(initialTab());
  let dialogCreateOpen = $state(false);
  // 手動タイムラインの編集対象 (entry カードの編集ボタンから設定)。
  let entryEditTarget = $state<DialogueLog | null>(null);
  let contractCreateOpen = $state(false);
  let contractEditTarget = $state<Contract | null>(null);
  let contractTransitionTarget = $state<Contract | null>(null);
  // 契約タブの表示切替: 通常契約 (アーカイブ除外) / アーカイブ済み。
  let contractView = $state<"normal" | "archived">("normal");
  let contractArchiveTarget = $state<Contract | null>(null);
  let contractRestoreTarget = $state<Contract | null>(null);
  // 契約作成 → 紐付けフロー (作成元アクション保持 / 紐付け再試行) は
  // features/contract-create/contract-link.svelte.ts へ共通化済み (firm/lawyer 両
  // ページで共有)。
  // 契約タブ上部の「受注済み・契約未作成」作業キュー。 SalesActionPanel の内部
  // 状態に依存せず、 won/completed を取得して派生状態 (needsContractCreation)
  // で絞る (契約未作成は saved status ではない)。
  let wonMissingActions = $state<SalesAction[]>([]);
  let wonMissingError = $state<string | null>(null);
  let wonMissingRequestSeq = 0;
  let contractFollowupFirmId = id;

  // データ取得は entity store に委譲する。ローカル $state は楽観更新 UI のみ保持。
  let lawyers = $derived(firmLawyersStore.data);
  let lawyersError = $derived(firmLawyersStore.error);
  let salesSolutions = $derived(productOptionsStore.data);
  let salesSolutionsError = $derived(productOptionsStore.error);
  // 該当事務所の商談 (タイムラインモーダルの関連商談プルダウン用)。
  let opportunities = $state<Opportunity[]>([]);
  // 取得済み/取得中の対象 firm_id。 同一コンポーネントのまま別事務所へ遷移した
  // 場合に前の事務所の候補を出さないよう、 firm 単位で再取得・競合ガードする。
  let opportunitiesFirmId = $state<string | null>(null);
  // 担当者マスタ (ACC-4)。契約一覧の担当者列を id でなく表示名で出すための候補。
  let accounts = $derived(assignableAccountsStore.data);
  let salesSolutionOptions = $derived(
    salesSolutions.map((p) => ({ id: p.id, label: p.name })),
  );
  // 契約未作成キューの product_id → ソリューション名解決。 SalesAction 側の
  // product_id は素の string なのでキーを string に広げる。
  let productNameById = $derived(
    new Map<string, string>(salesSolutions.map((p) => [p.id, p.name])),
  );
  // 契約対象弁護士 (lawyer_id) を名前表示するため、 所属弁護士のリストを再利用。
  let lawyerOptions = $derived(
    lawyers.map((l) => ({
      id: l.id,
      label: l.full_name,
    })),
  );
  // タイムライン参加者行の id → 名前解決。
  let lawyerNameById = $derived(
    Object.fromEntries(lawyers.map((l) => [l.id, l.full_name])),
  );
  // タイムラインの商談/契約カード詳細表示「操作者」の id → 名前解決。
  let accountNameById = $derived(
    Object.fromEntries(accounts.map((a) => [a.id, a.display_name])),
  );
  let proposalOptions = $derived(
    opportunities.map((o) => ({ id: o.id, name: o.name })),
  );
  let allowEdit = $derived(
    sessionStore.user ? canEdit(sessionStore.user.role) : false,
  );

  // ── 上位タブ件数バッジ (spec 20260613-tab-counts-and-review-badges PR1) ──
  // 件数は保存値にせず、既存データ・既存一覧 API から導出する。
  // - 営業アクション: SalesActionPanel と共有する salesActionListStore から
  //   isCurrentAction で導出 (内側「現在の次アクション (n)」と同じ意味。
  //   結果件数は含めない)。
  // - タイムライン: timelineStore.items (profile タブと共有)。
  // - 所属弁護士: lawyers.length。
  // - 契約 (有効のみ)・メモ: 一覧 API の total (page_size=1) を firm 単位で取得。
  //   null = 未取得 (読み込み中・取得失敗) でバッジ非表示。
  let activeContractCount = $state<number | null>(null);
  let memoCount = $state<number | null>(null);
  let pendingFormSubmissions = $state<FormSubmission[]>([]);
  let pendingFormSubmissionTargetId = $state<string | null>(null);
  let pendingFormSubmissionError = $state<string | null>(null);
  const emptyGenericTabUpdates: FirmGenericTabUpdates = {
    profile_timeline: false,
    lawyers: false,
    contracts: false,
    memos: false,
  };
  let genericTabUpdates = $state<FirmGenericTabUpdates>(emptyGenericTabUpdates);
  let genericTabUpdatesFirmId = $state<string | null>(null);
  let lastSeenMarkKey = $state<string | null>(null);
  // 件数取得済み/取得中の対象 firm_id (opportunities と同じ再入ガード)。
  let tabCountsFirmId = $state<string | null>(null);
  let salesCurrentCount = $derived(
    salesActionListStore.data === null
      ? null
      : salesActionListStore.data.items.filter(isCurrentAction).length,
  );
  let timelineCount = $derived(
    timelineStore.counts?.all ?? timelineStore.items.length,
  );

  // 担当者 (assignee) はヘッダー右上で表示・編集する。保存先は既存の
  // firms.assignee_account_id。候補は本ページで既にロード済みの accounts を
  // AccountSelect に渡し、N+1 fetch を避ける。
  let assigneeAccountId = $state("");
  let assigneeSaving = $state(false);
  $effect(() => {
    assigneeAccountId = firmDetailStore.data?.assignee_account_id ?? "";
  });
  async function saveAssignee(next: string): Promise<void> {
    if (assigneeSaving) return;
    const firmId = firmDetailStore.data?.id;
    if (!firmId) return;
    assigneeSaving = true;
    try {
      await patchFirm(firmId, {
        assignee_account_id: next === "" ? null : next,
      });
    } catch {
      // 失敗時は表示を保存値へ戻す (楽観更新の巻き戻し)。
      assigneeAccountId = firmDetailStore.data?.assignee_account_id ?? "";
    } finally {
      assigneeSaving = false;
    }
  }
  // 編集権限のないユーザー向けの担当者名解決。domain 関数への薄いアダプタ。
  // accounts には inactive も含むため無効化済みアカウントでも氏名表示できる。
  function assigneeDisplayName(accountId: string | null | undefined): string {
    return resolveAssigneeName(
      accountId,
      accounts,
      assignableAccountsStore.loaded,
    );
  }

  $effect(() => {
    void firmDetailStore.load(id);
    return () => {
      firmDetailStore.clear();
      timelineStore.clear();
      firmLawyersStore.clear();
      productOptionsStore.clear();
      assignableAccountsStore.clear();
      // 上位タブ件数 (salesCurrentCount) の導出元。残すと別事務所・弁護士詳細で
      // 一瞬前の事務所の件数・行が見えるため、他 store と同様にここで破棄する。
      salesActionListStore.clear();
      genericTabUpdates = emptyGenericTabUpdates;
      genericTabUpdatesFirmId = null;
      lastSeenMarkKey = null;
      pendingFormSubmissions = [];
      pendingFormSubmissionTargetId = null;
      pendingFormSubmissionError = null;
      // 契約作成 → 紐付けフローは firm/lawyer 両ページで共有するシングルトン。
      // 別種のページ (弁護士詳細等) へ遷移する完全アンマウントでも破棄する。
      contractLinkFlow.reset();
    };
  });

  $effect(() => {
    if (firmDetailStore.data?.id === id) {
      void firmLawyersStore.load(id);
    }
  });

  $effect(() => {
    if (firmDetailStore.data?.id === id && hasCompetitorUsageHash()) {
      activeTab = "profile";
    }
  });

  $effect(() => {
    if (contractFollowupFirmId === id) return;
    contractFollowupFirmId = id;
    clearContractFollowupState();
  });

  // profile タブを開いた時、 接点履歴 (Timeline) は左/右 2 カラムの右側で
  // 常時描画するため、 タブ切替に依存せず profile タブ時に取得する。
  $effect(() => {
    if (activeTab === "profile" && firmDetailStore.data?.id === id) {
      void timelineStore.loadFirm(id);
      scrollToCompetitorUsage();
    }
  });

  $effect(() => {
    if (activeTab === "deals" && firmDetailStore.data?.id === id) {
      void contractListStore.refresh({
        firm_id: id,
        archive_filter: contractView === "archived" ? "only" : "exclude",
        page: 1,
        page_size: 200,
        sort_by: "updated_at",
        sort_dir: "desc",
      });
    }
  });

  $effect(() => {
    if (activeTab === "deals" && firmDetailStore.data?.id === id) {
      void refreshWonMissing(id);
    }
  });

  // 商品マスタは契約一覧 (deals タブ) と営業アクション (sales タブ) の
  // 両方で product_id → name 解決に使うので、 firm 詳細を開いた時点で 1 回
  // load しておく。 タブを開いてから fetch すると 「ID のまま見える時間」 が
  // 発生して旧 UI の挙動と乖離するため。
  $effect(() => {
    if (firmDetailStore.data?.id === id && salesSolutions.length === 0) {
      void productOptionsStore.load();
    }
  });

  // 担当者マスタも firm 詳細を開いた時点で 1 回 load (契約一覧の担当者表示用)。
  // 取得失敗でも loaded フラグが立つため再試行ループは起きない (store 内で担保)。
  $effect(() => {
    if (firmDetailStore.data?.id === id && !assignableAccountsStore.loaded) {
      void assignableAccountsStore.load();
    }
  });

  // 該当事務所の商談はタイムライン追加/編集モーダルの関連商談プルダウンで使う。
  $effect(() => {
    if (firmDetailStore.data?.id === id && opportunitiesFirmId !== id) {
      opportunitiesFirmId = id;
      opportunities = [];
      void loadOpportunities(id);
    }
  });

  // 上位タブの件数は詳細を開いた時点で全タブ分そろえる (タブを開く前に中身の
  // 有無を判断できるようにする)。営業アクション / タイムラインは各タブの
  // コンポーネントと store を共有するため、初期タブ側でロードされる場合は
  // 二重 fetch しない。
  $effect(() => {
    if (firmDetailStore.data?.id === id && tabCountsFirmId !== id) {
      tabCountsFirmId = id;
      activeContractCount = null;
      memoCount = null;
      void refreshActiveContractCount(id);
      void loadMemoCount(id);
      if (activeTab !== "sales") void refreshSalesActionsForCount(id);
      if (activeTab !== "profile") void timelineStore.loadFirm(id);
    }
  });

  $effect(() => {
    if (!formCrmEnabled) return;
    if (
      firmDetailStore.data?.id === id &&
      pendingFormSubmissionTargetId !== id
    ) {
      pendingFormSubmissionTargetId = id;
      pendingFormSubmissions = [];
      pendingFormSubmissionError = null;
      void loadPendingFormSubmissionsForFirm(id);
    }
  });

  $effect(() => {
    if (firmDetailStore.data?.id === id && genericTabUpdatesFirmId !== id) {
      genericTabUpdatesFirmId = id;
      genericTabUpdates = emptyGenericTabUpdates;
      lastSeenMarkKey = null;
      void loadGenericTabUpdates(id);
    }
  });

  $effect(() => {
    if (firmDetailStore.data?.id === id) {
      void markActiveGenericTabSeen(id);
    }
  });

  // SalesActionPanel (sales タブ) と同じクエリで共有 store を更新する。
  // クエリが異なるとパネル表示と上位タブ件数の意味がずれるため一致させる。
  function refreshSalesActionsForCount(firmId: string): Promise<void> {
    return salesActionListStore.refresh({
      target_kind: "firm",
      firm_id: firmId,
      page_size: 200,
      sort_by: "due_date",
      sort_dir: "asc",
    });
  }

  // 契約タブの件数 = 有効契約のみ (status=active、アーカイブ済みは API 既定で
  // 除外)。total だけ使うので page_size=1。失敗時は直前の値を保持する
  // (件数バッジは付加情報。失敗しても契約タブ自体は使える)。
  async function refreshActiveContractCount(firmId: string): Promise<void> {
    try {
      const page = parsePaged(
        await listContracts({
          firm_id: firmId,
          status: "active",
          page: 1,
          page_size: 1,
        }),
        parseContract,
      );
      if (firmId !== id) return;
      activeContractCount = page.total;
    } catch {
      // 失敗時は null (非表示) / 直前値のまま。
    }
  }

  // メモタブの件数。削除済み除外は API 側 (deleted_at IS NULL)。メモタブを
  // 開いた後は CustomerMemoPanel の onCountChange が最新値を反映する。
  async function loadMemoCount(firmId: string): Promise<void> {
    try {
      const page = await listFirmMemos(firmId, { page: 1, page_size: 1 });
      if (firmId !== id) return;
      memoCount = page.total;
    } catch {
      // 失敗時は null (非表示) / 直前値のまま。
    }
  }

  async function loadPendingFormSubmissionsForFirm(firmId: string): Promise<void> {
    if (!formCrmEnabled) return;
    try {
      const res = await listFormSubmissions({
        matched_firm_id: firmId,
        field_update_status: "pending",
        limit: 5,
      });
      if (firmId !== id || firmDetailStore.data?.id !== firmId) return;
      pendingFormSubmissions = res.items;
      pendingFormSubmissionError = null;
    } catch (err) {
      if (firmId !== id) return;
      pendingFormSubmissionError = err instanceof Error ? err.message : String(err);
    }
  }

  function pendingFormSubmissionCountLabel(): string {
    return pendingFormSubmissions.length >= 5
      ? `${pendingFormSubmissions.length}件以上`
      : `${pendingFormSubmissions.length}件`;
  }

  function formSubmissionReceivedLabel(submission: FormSubmission): string {
    return submission.received_at.slice(0, 16).replace("T", " ");
  }

  function customerTabKeyFor(active: TabKey): GenericCustomerTabKey | null {
    if (active === "profile") return "profile_timeline";
    if (active === "lawyers") return "lawyers";
    if (active === "deals") return "contracts";
    if (active === "memos") return "memos";
    return null;
  }

  function hasGenericTabUpdate(active: TabKey): boolean {
    const tabKey = customerTabKeyFor(active);
    return tabKey ? genericTabUpdates[tabKey] : false;
  }

  function clearGenericTabUpdate(tabKey: GenericCustomerTabKey): void {
    genericTabUpdates = { ...genericTabUpdates, [tabKey]: false };
  }

  async function loadGenericTabUpdates(firmId: string): Promise<void> {
    try {
      const updates = await getFirmGenericTabUpdates(firmId);
      if (firmId !== id || firmDetailStore.data?.id !== firmId) return;
      genericTabUpdates = updates;
      const activeCustomerTab = customerTabKeyFor(activeTab);
      if (activeCustomerTab) clearGenericTabUpdate(activeCustomerTab);
    } catch {
      if (firmId === id) genericTabUpdates = emptyGenericTabUpdates;
    }
  }

  async function markActiveGenericTabSeen(firmId: string): Promise<void> {
    const tabKey = customerTabKeyFor(activeTab);
    if (!tabKey) return;
    const markKey = `${firmId}:${tabKey}`;
    if (lastSeenMarkKey === markKey) return;
    lastSeenMarkKey = markKey;
    clearGenericTabUpdate(tabKey);
    try {
      await markTabSeen({
        target_kind: "firm",
        target_id: firmId,
        tab_key: tabKey,
      });
    } catch {
      // 閲覧状態は付加表示。失敗してもタブ本体の利用は妨げない。
    }
  }

  async function loadOpportunities(firmId: string) {
    try {
      const page = parsePaged(
        await listOpportunities({
          firm_id: firmId,
          page: 1,
          page_size: 200,
          sort_by: "created_at",
          sort_dir: "desc",
        }),
        parseOpportunity,
      );
      // 別事務所へ遷移済みなら古いレスポンスを捨てる。
      if (opportunitiesFirmId === firmId) opportunities = page.items;
    } catch {
      // 関連商談プルダウンは付加情報。失敗してもタイムライン自体は使える。
      if (opportunitiesFirmId === firmId) opportunities = [];
    }
  }

  function refreshContracts() {
    void contractListStore.refresh({
      firm_id: id,
      archive_filter: contractView === "archived" ? "only" : "exclude",
      page: 1,
      page_size: 200,
      sort_by: "updated_at",
      sort_dir: "desc",
    });
  }

  function clearContractFollowupState() {
    wonMissingRequestSeq += 1;
    wonMissingActions = [];
    wonMissingError = null;
    contractLinkFlow.reset();
  }

  async function refreshWonMissing(firmId = id) {
    const requestSeq = ++wonMissingRequestSeq;
    try {
      const page = await listSalesActions({
        target_kind: "firm",
        firm_id: firmId,
        sales_status: "won",
        action_status: "completed",
        page_size: 200,
        sort_by: "updated_at",
        sort_dir: "desc",
      });
      if (requestSeq !== wonMissingRequestSeq || firmId !== id) return;
      wonMissingActions = page.items.filter(needsContractCreation);
      wonMissingError = null;
    } catch (err) {
      if (requestSeq !== wonMissingRequestSeq || firmId !== id) return;
      wonMissingError = err instanceof Error ? err.message : String(err);
    }
  }

  // 結果タブ / 契約タブの「契約を作成する」CTA。 作成元の受注アクションを保持して
  // 契約作成モーダルを開く。 キャンセル時は何も PATCH しないため、 契約未作成の
  // 表示はそのまま残る。
  function openContractCreateFrom(action: SalesAction) {
    contractLinkFlow.startFrom(action);
    contractView = "normal";
    activeTab = "deals";
    contractCreateOpen = true;
  }

  // 契約作成成功後、 作成元の受注アクションがあれば linked_contract_id を紐付ける
  // (共通化フロー / features/contract-create/contract-link.svelte.ts)。 PATCH に
  // 失敗しても契約データは削除せず、 紐付けだけ再試行できる。 契約タブの作業キューは
  // 紐付け成否を問わず最新化する (結果タブ側は SalesActionPanel がタブ再表示時に
  // 再取得するため、 ここでの個別更新は不要)。 別事務所へ遷移済みなら (isStale)
  // 状態更新・再取得どちらも行わない。
  function handleContractCreated(contract: Contract) {
    contractCreateOpen = false;
    const firmId = id;
    refreshContracts();
    void refreshActiveContractCount(id);
    void refreshTimeline();
    void contractLinkFlow.handleCreated(
      contract,
      () => refreshWonMissing(firmId),
      () => firmId !== id,
    );
  }

  function retryContractLink() {
    const firmId = id;
    void contractLinkFlow.retryLink(
      () => refreshWonMissing(firmId),
      () => firmId !== id,
    );
  }

  function participantNames(action: SalesAction): string {
    if (action.participant_lawyer_ids.length === 0) return "—";
    return action.participant_lawyer_ids
      .map((lid) => lawyerNameById[lid] ?? lid)
      .join("、 ");
  }

  function wonMissingSolutionName(action: SalesAction): string {
    if (!action.product_id) return "未設定";
    return productNameById.get(action.product_id) ?? action.product_id;
  }

  function handleContractUpdated() {
    contractEditTarget = null;
    refreshContracts();
    void refreshActiveContractCount(id);
    void refreshTimeline();
  }

  function handleContractTransitioned() {
    contractTransitionTarget = null;
    refreshContracts();
    void refreshActiveContractCount(id);
    void refreshTimeline();
  }

  function handleContractArchived() {
    contractArchiveTarget = null;
    refreshContracts();
    void refreshActiveContractCount(id);
    void refreshTimeline();
  }

  function handleContractRestored() {
    contractRestoreTarget = null;
    refreshContracts();
    void refreshActiveContractCount(id);
    void refreshTimeline();
  }

  async function refreshTimeline() {
    await timelineStore.loadFirm(id, timelineStore.query);
  }

  function handleTimelineFilterChange(query: TimelineQuery): void {
    void timelineStore.loadFirm(id, query);
  }

  function handleTimelineLoadMore(): void {
    void timelineStore.loadMore();
  }

  /// 代表弁護士集合の全置換 (空配列 = 代表 0 名も正常)。 保存後は firm detail と
  /// lawyers list を再取得し、 基本情報と所属弁護士タブの代表表示を一致させる
  /// (spec 20260612-multiple-firm-representative-lawyers)。
  async function handleRepresentativesChanged(lawyerIds: string[]) {
    await setFirmRepresentatives(id, lawyerIds);
    await firmDetailStore.load(id);
    await firmLawyersStore.load(id);
  }

  function handleEntryCreated() {
    dialogCreateOpen = false;
    void refreshTimeline();
    // 最終接触は接点履歴の弁護士単位集計のため、所属弁護士タブの表示も追随させる。
    void firmLawyersStore.load(id);
  }

  // 編集保存成功時: モーダルを閉じてタイムラインを再取得する。
  // Timeline コンポーネントは再マウントされないため、フィルタ状態は維持される。
  function handleEntryUpdated() {
    entryEditTarget = null;
    void refreshTimeline();
    // 最終接触は接点履歴の弁護士単位集計のため、所属弁護士タブの表示も追随させる。
    void firmLawyersStore.load(id);
  }
</script>

<p class="back-link"><a href="/firms">← 法律事務所一覧へ</a></p>

{#if firmDetailStore.loading && firmDetailStore.data === null}
  <p class="empty">読み込み中…</p>
{:else if firmDetailStore.error}
  <p class="error">{firmDetailStore.error}</p>
{:else if firmDetailStore.data}
  {@const firm = firmDetailStore.data}
  {@const bulkSalesLabel = firmBulkSalesSummaryLabel(firm.bulk_sales_decision)}
  <!--
    法律事務所詳細ヘッダー。 src/components/crm/FirmHeader.svelte の v2 仕様に準拠:
    .panel firm-header コンテナ / h2 / 一括営業対象外・競合 (.tag-danger) + 競合の詳細 .alert
    / 更新日 (右寄せ)。 一括営業対象外のタグは backend の派生値 (事務所 5 フラグ由来) の
    集約ラベル (spec 20260716「Header and List Labels」、 由来表記なし)。 旧営業NG理由は
    ヘッダに出さず、 営業対象外設定カード内の read-only 表示のみ。
    右上は誤操作リスク低減のため削除導線を出さず、 営業担当 (1 行) + 更新日 (1 行)
    の 2 行表示に統一する (削除は backend DELETE / firm-delete feature は存置)。
  -->
  <div class="panel firm-header">
    <div class="title-row">
      <div class="title-main">
        <h2>{firm.official_name}</h2>
        {#if bulkSalesLabel || firm.is_competitor}
          <div class="tag-row">
            {#if bulkSalesLabel}
              <span class="tag tag-danger" data-testid="firm-header-bulk-sales-tag"
                >{bulkSalesLabel}</span
              >
            {/if}
            {#if firm.is_competitor}
              <span class="tag tag-danger">競合</span>
            {/if}
          </div>
        {/if}
      </div>
      <div class="title-right">
        <div class="assignee" data-testid="firm-header-assignee">
          <span class="assignee-label">担当者</span>
          {#if allowEdit}
            <AccountSelect
              bind:value={assigneeAccountId}
              {accounts}
              disabled={assigneeSaving}
              placeholder="(未割当)"
              onChange={(next) => void saveAssignee(next)}
            />
            {#if assigneeSaving}<span class="assignee-saving">保存中…</span
              >{/if}
          {:else}
            <span class="assignee-value"
              >{assigneeDisplayName(firm.assignee_account_id)}</span
            >
          {/if}
        </div>
        <div class="meta">更新: {formatHeaderDate(firm.updated_at)}</div>
      </div>
    </div>
    {#if firm.is_competitor}
      <div class="alert">
        競合: {reasonOrUnset(firm.competitor_reason)}
      </div>
    {/if}
  </div>

  {#if pendingFormSubmissions.length > 0 || pendingFormSubmissionError}
    <section class="pending-form-alert" aria-label="要承認のフォーム送信">
      <div class="pending-form-alert-head">
        <span>要承認のフォーム送信</span>
        {#if pendingFormSubmissions.length > 0}
          <strong>{pendingFormSubmissionCountLabel()}</strong>
        {/if}
      </div>
      {#if pendingFormSubmissionError}
        <p class="pending-form-error">{pendingFormSubmissionError}</p>
      {:else}
        <div class="pending-form-links">
          {#each pendingFormSubmissions as submission (submission.id)}
            <a href={`/form-submissions/${submission.id}`}>
              {formSubmissionReceivedLabel(submission)}
            </a>
          {/each}
        </div>
      {/if}
    </section>
  {/if}

  <!--
    上位タブ件数バッジ (spec 20260613-tab-counts-and-review-badges PR1)。
    1 件以上のタブだけ中立色バッジを表示 (0 件は非表示)。バッジは aria-hidden
    なので、読み上げ用にボタン側へ「タブ名 n件」の aria-label を付ける。
  -->
  <nav class="tabs" aria-label="詳細タブ">
    <button
      type="button"
      class:active={activeTab === "sales"}
      data-testid="firm-tab-sales-action"
      aria-label={tabCountAriaLabel("営業アクション", salesCurrentCount)}
      onclick={() => (activeTab = "sales")}
    >
      営業アクション<TabCountBadge
        count={salesCurrentCount}
        testId="firm-tab-count-sales"
      />
    </button>
    <button
      type="button"
      class:active={activeTab === "profile"}
      aria-label={tabStatusAriaLabel(
        "項目情報・タイムライン",
        timelineCount,
        hasGenericTabUpdate("profile"),
      )}
      onclick={() => (activeTab = "profile")}
    >
      項目情報・タイムライン<TabCountBadge
        count={timelineCount}
        testId="firm-tab-count-timeline"
      /><TabUpdateDot
        show={hasGenericTabUpdate("profile")}
        testId="firm-tab-update-profile-timeline"
      />
    </button>
    <button
      type="button"
      class:active={activeTab === "lawyers"}
      aria-label={tabStatusAriaLabel(
        "所属弁護士",
        lawyers.length,
        hasGenericTabUpdate("lawyers"),
      )}
      onclick={() => {
        activeTab = "lawyers";
        // 営業タブでのアクション更新等を反映するため、タブ表示時に再取得する
        // (SalesActionPanel のタブ再表示時再取得と同じ考え方。 store は取得完了
        // まで既存データを保持するためちらつかない)。
        void firmLawyersStore.load(id);
      }}
    >
      所属弁護士<TabCountBadge
        count={lawyers.length}
        testId="firm-tab-count-lawyers"
      /><TabUpdateDot
        show={hasGenericTabUpdate("lawyers")}
        testId="firm-tab-update-lawyers"
      />
    </button>
    <button
      type="button"
      class:active={activeTab === "deals"}
      aria-label={tabStatusAriaLabel(
        "契約",
        activeContractCount,
        hasGenericTabUpdate("deals"),
      )}
      onclick={() => (activeTab = "deals")}
    >
      契約<TabCountBadge
        count={activeContractCount}
        testId="firm-tab-count-contracts"
      /><TabUpdateDot
        show={hasGenericTabUpdate("deals")}
        testId="firm-tab-update-contracts"
      />
    </button>
    <button
      type="button"
      class:active={activeTab === "memos"}
      data-testid="firm-tab-memos"
      aria-label={tabStatusAriaLabel(
        "メモ",
        memoCount,
        hasGenericTabUpdate("memos"),
      )}
      onclick={() => (activeTab = "memos")}
    >
      メモ<TabCountBadge
        count={memoCount}
        testId="firm-tab-count-memos"
      /><TabUpdateDot
        show={hasGenericTabUpdate("memos")}
        testId="firm-tab-update-memos"
      />
    </button>
  </nav>

  {#if activeTab === "profile"}
    <div class="detail-profile-grid">
      <section class="profile-summary" aria-label="項目情報">
        <FirmSummary
          {firm}
          canEdit={allowEdit}
          representativeCandidates={lawyers}
          onRepresentativesChange={handleRepresentativesChanged}
        />
      </section>
      <section class="profile-timeline" aria-label="タイムライン">
        <header class="timeline-header">
          <h3>タイムライン</h3>
          <div class="timeline-actions">
            <button
              type="button"
              class="btn"
              disabled={!allowEdit}
              onclick={() => (dialogCreateOpen = true)}
            >
              + タイムラインに追加
            </button>
          </div>
        </header>
        <Timeline
          items={timelineStore.items}
          counts={timelineStore.counts}
          loading={timelineStore.loading}
          error={timelineStore.error}
          {lawyerNameById}
          {accountNameById}
          showContactLogLawyer
          onFilterChange={handleTimelineFilterChange}
          onLoadMore={handleTimelineLoadMore}
          canLoadMore={timelineStore.canLoadMore}
          onEditEntry={allowEdit
            ? (entry) => (entryEditTarget = entry)
            : undefined}
        />
      </section>
    </div>
  {:else if activeTab === "sales"}
    <section class="sales-action-stack" aria-label="提案・アクション一覧">
      {#if salesSolutionsError}<p class="error">{salesSolutionsError}</p>{/if}
      <SalesActionPanel
        target={{ kind: "firm", id: firm.id }}
        canEdit={allowEdit}
        solutions={salesSolutionOptions}
        {lawyers}
        onCreateContract={(action) => openContractCreateFrom(action)}
        resultCreateContractCta
      />
    </section>
  {:else if activeTab === "lawyers"}
    <section class="tab-body" aria-label="所属弁護士">
      {#if lawyersError}<p class="error">{lawyersError}</p>{/if}
      <FirmLawyers
        {lawyers}
        representativeIds={firm.representative_lawyer_ids}
        canEdit={allowEdit}
      />
    </section>
  {:else if activeTab === "deals"}
    <section class="tab-body" aria-label="契約">
      <header class="tab-header">
        <h3>契約</h3>
        {#if contractView === "normal"}
          <button
            type="button"
            class="btn btn-primary"
            disabled={!allowEdit}
            onclick={() => {
              contractLinkFlow.clearSource();
              contractCreateOpen = true;
            }}
          >
            + 契約を追加
          </button>
        {/if}
      </header>
      <div
        class="contract-view-toggle"
        role="group"
        aria-label="契約表示の切替"
      >
        <button
          type="button"
          class:active={contractView === "normal"}
          onclick={() => (contractView = "normal")}
        >
          通常契約
        </button>
        <button
          type="button"
          class:active={contractView === "archived"}
          onclick={() => (contractView = "archived")}
        >
          アーカイブ済み
        </button>
      </div>
      <!--
        受注済み・契約未作成の作業キュー。 契約一覧の一部ではなく、 契約作成待ちの
        受注アクション (派生状態 won_contract_missing) を最上部で見せる。
      -->
      {#if contractView === "normal" && (wonMissingActions.length > 0 || contractLinkFlow.error || wonMissingError)}
        <section
          class="won-missing-panel"
          aria-label="受注済み・契約未作成"
          data-testid="won-contract-missing-section"
        >
          <h4>受注済み・契約未作成</h4>
          {#if wonMissingError}
            <p class="error">{wonMissingError}</p>
          {/if}
          {#if contractLinkFlow.error}
            <div class="link-error">
              <p class="error">
                契約は作成されましたが、営業アクションへの紐付けに失敗しました:
                {contractLinkFlow.error}
              </p>
              {#if contractLinkFlow.retry}
                <button
                  type="button"
                  class="btn"
                  data-testid="contract-link-retry"
                  onclick={retryContractLink}
                >
                  紐付けを再試行
                </button>
              {/if}
            </div>
          {/if}
          {#if wonMissingActions.length > 0}
            <ul class="won-missing-list">
              {#each wonMissingActions as action (action.id)}
                <li
                  class="won-missing-row"
                  data-testid="won-contract-missing-row"
                >
                  <span class="wm-solution">
                    {wonMissingSolutionName(action)}
                  </span>
                  <span class="wm-meta">
                    完了日 {action.completed_at
                      ? action.completed_at.slice(0, 10)
                      : "—"}
                  </span>
                  <span class="wm-meta">対象 {participantNames(action)}</span>
                  <button
                    type="button"
                    class="btn btn-primary"
                    disabled={!allowEdit}
                    onclick={() => openContractCreateFrom(action)}
                  >
                    契約を作成する
                  </button>
                </li>
              {/each}
            </ul>
          {/if}
        </section>
      {/if}
      {#if contractListStore.loading && contractListStore.data === null}
        <p class="empty">読み込み中…</p>
      {:else if contractListStore.error}
        <p class="error">{contractListStore.error}</p>
      {:else if contractListStore.data}
        <ContractTable
          contracts={contractListStore.data.items}
          sortBy="updated_at"
          sortDir="desc"
          onSort={() => {}}
          hideFirm
          canEdit={allowEdit}
          archived={contractView === "archived"}
          {lawyerOptions}
          accountOptions={accounts}
          onEdit={contractView === "archived"
            ? undefined
            : (c) => (contractEditTarget = c)}
          onTransition={contractView === "archived"
            ? undefined
            : (c) => (contractTransitionTarget = c)}
          onArchive={contractView === "archived"
            ? undefined
            : (c) => (contractArchiveTarget = c)}
          onRestore={contractView === "archived"
            ? (c) => (contractRestoreTarget = c)
            : undefined}
        />
      {/if}
    </section>
  {:else if activeTab === "memos"}
    <section class="tab-body" aria-label="メモ">
      <CustomerMemoPanel
        target={{ kind: "firm", id: firm.id }}
        canEdit={allowEdit}
        onCountChange={(count) => (memoCount = count)}
      />
    </section>
  {/if}

  <AddDialogueLogModal
    open={dialogCreateOpen}
    firmId={firm.id}
    {lawyers}
    proposals={proposalOptions}
    onClose={() => (dialogCreateOpen = false)}
    onCreated={handleEntryCreated}
  />
  {#if entryEditTarget}
    <AddDialogueLogModal
      open={true}
      mode="edit"
      initial={entryEditTarget}
      firmId={firm.id}
      {lawyers}
      proposals={proposalOptions}
      onClose={() => (entryEditTarget = null)}
      onUpdated={handleEntryUpdated}
    />
  {/if}
  <ContractCreateModal
    open={contractCreateOpen}
    fixedFirmId={firm.id}
    {opportunities}
    initialRelatedLawyerIds={contractLinkFlow.sourceAction?.participant_lawyer_ids ??
      []}
    LineItemsEditor={ContractLineItemsEditor}
    lineItemsCanEdit={allowEdit}
    onClose={() => {
      contractCreateOpen = false;
      contractLinkFlow.clearSource();
    }}
    onCreated={handleContractCreated}
  />
  {#if contractEditTarget}
    <ContractCreateModal
      open={true}
      mode="edit"
      initial={contractEditTarget}
      fixedFirmId={firm.id}
      {opportunities}
      LineItemsEditor={ContractLineItemsEditor}
      lineItemsCanEdit={allowEdit}
      onClose={() => {
        contractEditTarget = null;
      }}
      onUpdated={handleContractUpdated}
    />
  {/if}
  {#if contractTransitionTarget}
    <ContractTransitionModal
      open={true}
      contract={contractTransitionTarget}
      onClose={() => (contractTransitionTarget = null)}
      onChanged={handleContractTransitioned}
    />
  {/if}
  {#if contractArchiveTarget}
    <ContractArchiveModal
      open={true}
      contract={contractArchiveTarget}
      onClose={() => (contractArchiveTarget = null)}
      onArchived={handleContractArchived}
    />
  {/if}
  {#if contractRestoreTarget}
    <ContractRestoreModal
      open={true}
      contract={contractRestoreTarget}
      onClose={() => (contractRestoreTarget = null)}
      onRestored={handleContractRestored}
    />
  {/if}
{/if}

<style>
  /*
    scoped style は global.css (.btn / .panel / .tag / .tabs / .empty / .error)
    と被らない範囲だけ持つ。 ヘッダー / sticky 左カラム / 品質警告 / グリッド系。
  */
  .back-link {
    margin: 0 0 8px;
  }
  .back-link a {
    font-size: 0.875rem;
  }

  /* ─────────────────────────────────────────────
   * Firm header (src/components/crm/FirmHeader.svelte 準拠)
   * .panel は global.css で定義済み。
   * ───────────────────────────────────────────── */
  .firm-header {
    margin-bottom: 12px;
  }
  .title-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
  }
  .title-main {
    flex: 1 1 360px;
    min-width: 0;
  }
  .title-main h2 {
    margin: 0 0 4px 0;
  }
  .title-main :global(.muted) {
    font-size: 13px;
  }
  .tag-row {
    margin-top: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  .title-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex: 0 0 auto;
    max-width: 100%;
    gap: 8px;
  }
  .meta {
    text-align: right;
    font-size: 12px;
    color: var(--muted);
    white-space: nowrap;
  }
  /* 担当者 (assignee) は更新日の上に右寄せで置く。 */
  .assignee {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 6px;
  }
  .assignee-label {
    font-size: 12px;
    font-weight: 700;
    color: var(--muted);
  }
  .assignee-value {
    font-size: 13px;
    color: #111827;
  }
  .assignee-saving {
    font-size: 11px;
    color: var(--muted);
  }
  .alert {
    margin-top: 12px;
    padding: 8px 12px;
    background: #fef2f2;
    border-left: 3px solid var(--danger);
    border-radius: 4px;
    color: #7f1d1d;
    font-size: 13px;
  }
  .pending-form-alert {
    display: grid;
    gap: 8px;
    margin: 0 0 12px;
    padding: 10px 12px;
    background: #fffbeb;
    border: 1px solid #fcd34d;
    border-radius: 6px;
    color: #92400e;
    font-size: 13px;
  }
  .pending-form-alert-head {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    font-weight: 700;
  }
  .pending-form-alert-head strong {
    font-size: 12px;
  }
  .pending-form-links {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .pending-form-links a {
    color: #1d4ed8;
    font-weight: 700;
  }
  .pending-form-error {
    margin: 0;
    color: #7f1d1d;
  }

  /* ─────────────────────────────────────────────
   * Tabs - global .tabs に追加で flex-wrap だけ被せる
   * (狭幅でタブが折り返せるように)。
   * ───────────────────────────────────────────── */
  .tabs {
    flex-wrap: wrap;
  }

  /* ─────────────────────────────────────────────
   * profile タブの 2 カラムグリッド (src/pages/firms/[id].astro 準拠)
   * 左カラムは sticky で追従。
   * ───────────────────────────────────────────── */
  .detail-profile-grid {
    display: grid;
    /* 左カラム (項目情報) は窮屈さを解消するため右カラム比 約1.5倍に拡幅。 */
    grid-template-columns: minmax(420px, 4.5fr) minmax(0, 5.5fr);
    gap: 16px;
    align-items: start;
  }
  .profile-summary,
  .profile-timeline {
    min-width: 0;
  }
  .profile-summary {
    position: sticky;
    /* Sticky creates a stacking context. Keep modals rendered inside the left
       column above the timeline column instead of being painted underneath it. */
    z-index: 1;
    top: 16px;
    max-height: calc(100vh - 32px);
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 2px;
  }
  .profile-summary:global(:has(.modal-backdrop)) {
    overflow-y: hidden;
  }
  .sales-action-stack {
    display: grid;
    gap: 16px;
  }

  /* ─────────────────────────────────────────────
   * Right column header (タイムライン / タブ内ヘッダー)
   * ───────────────────────────────────────────── */
  .timeline-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }
  .timeline-header h3 {
    margin: 0;
    font-size: 0.875rem;
    color: #374151;
  }
  .timeline-actions {
    display: flex;
    gap: 8px;
  }
  .tab-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .tab-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .tab-header h3 {
    margin: 0;
    font-size: 0.875rem;
    color: #374151;
  }
  .contract-view-toggle {
    display: inline-flex;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    overflow: hidden;
    width: fit-content;
  }
  .contract-view-toggle button {
    padding: 6px 14px;
    border: 0;
    background: white;
    color: #4b5563;
    cursor: pointer;
    font: inherit;
    font-size: 0.8125rem;
  }
  .contract-view-toggle button + button {
    border-left: 1px solid #d1d5db;
  }
  .contract-view-toggle button.active {
    background: #2563eb;
    color: white;
  }

  /* ─────────────────────────────────────────────
   * 受注済み・契約未作成の作業キュー (契約タブ上部)
   * ───────────────────────────────────────────── */
  .won-missing-panel {
    border: 1px solid #fcd34d;
    background: #fffbeb;
    border-radius: 6px;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .won-missing-panel h4 {
    margin: 0;
    font-size: 0.875rem;
    color: #92400e;
  }
  .won-missing-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .won-missing-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    padding: 6px 0;
    border-top: 1px solid #fde68a;
    font-size: 0.875rem;
  }
  .won-missing-row:first-child {
    border-top: 0;
  }
  .wm-solution {
    font-weight: 600;
    color: #111827;
  }
  .wm-meta {
    color: #6b7280;
    font-size: 0.8125rem;
  }
  .won-missing-row .btn {
    margin-left: auto;
  }
  .link-error {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
  .link-error p {
    margin: 0;
  }

  @media (max-width: 1024px) {
    .detail-profile-grid {
      grid-template-columns: 1fr;
    }
    .profile-summary {
      position: static;
      z-index: auto;
      max-height: none;
      overflow: visible;
      padding-right: 0;
    }
  }
</style>
